import { useEffect, useRef } from "react";
import { Input } from "@heroui/react";
import { Rocket, FileText, CircleCheck, Trash2 } from "lucide-react";
import UserAssignDropdown from "./UserAssignDropdown";
import DatePickerDropdown from "./DatePickerDropdown";
import StatusDropdown from "./StatusDropdown";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";

const resizeDescriptionTextarea = (element) => {
  if (!element) return;
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
};

const AccountPlanDescriptionTextarea = ({
  value,
  onChange,
  placeholder,
  className = "",
}) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    resizeDescriptionTextarea(textareaRef.current);
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      aria-label={placeholder || "Action description"}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        resizeDescriptionTextarea(e.target);
      }}
      placeholder={placeholder}
      rows={1}
      className={`w-full min-h-[20px] resize-none overflow-hidden bg-transparent outline-none text-[0.763rem] font-inter text-gray-800 dark:text-gray-100 whitespace-normal break-words leading-5 ${className}`}
    />
  );
};

export const AccountPlanNewActionInput = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5 group text-gray-500 dark:text-gray-400 border-b border-gray-300 dark:border-gray-700 focus-within:border-secondary transition-colors duration-300 pb-2">
    <Rocket
      size={18}
      className="shrink-0 text-gray-500 dark:text-gray-400 group-focus-within:text-secondary transition-colors duration-300"
      strokeWidth={1.7}
    />
    <Input
      aria-label="Provide Action"
      placeholder="Provide Action"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="bordered"
      classNames={{
        input:
          "text-[0.763rem] font-inter !text-gray-800 dark:!text-gray-100 focus-within:text-secondary transition-colors duration-300",
        inputWrapper:
          "border-none shadow-none bg-transparent hover:border-none group-data-[focus=true]:border-none !p-0 !m-0 min-h-0 h-auto",
      }}
    />
  </div>
);

export const AccountPlanNewDescriptionInput = ({ value, onChange }) => (
  <div className="flex items-start gap-1.5 group text-gray-500 dark:text-gray-400 border-b border-gray-300 dark:border-gray-700 focus-within:border-secondary transition-colors duration-300 pb-2">
    <FileText
      size={18}
      className="mt-0.5 shrink-0 text-gray-500 dark:text-gray-400 group-focus-within:text-secondary transition-colors duration-300"
      strokeWidth={1.7}
    />
    <AccountPlanDescriptionTextarea
      value={value}
      onChange={onChange}
      placeholder="Enter description"
      className="placeholder:text-gray-500 dark:placeholder:text-gray-400"
    />
  </div>
);

export const AccountPlanOwnerField = ({ value, users, onAssign }) => (
  <UserAssignDropdown value={value} contacts={users} onAssign={onAssign} />
);

export const AccountPlanDueDateField = ({ value, onChange }) => (
  <DatePickerDropdown value={value} onChange={onChange} />
);

export const AccountPlanStatusField = ({ value, onChange }) => (
  <StatusDropdown value={value} onChange={onChange} />
);

export const AccountPlanAddButton = ({ onCommit }) => (
  <SecondaryButton
    title=""
    onClick={onCommit}
    className="shadow-none border-none p-1 rounded-full m-w-0"
    icon={
      <CircleCheck size={16} strokeWidth={2} className="text-success-500" />
    }
  />
);

export const AccountPlanEditableActionInput = ({ value, onChange }) => (
  <Input
    aria-label="Action"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    variant="bordered"
    classNames={{
      input: "text-[0.763rem] font-inter text-gray-900 dark:text-gray-100 bg-transparent",
      inputWrapper:
        "border-none shadow-none bg-transparent hover:border-gray-200 dark:hover:border-gray-700 group-data-[focus=true]:border-secondary min-h-0 h-auto",
    }}
  />
);

export const AccountPlanEditableDescriptionInput = ({ value, onChange }) => (
  <AccountPlanDescriptionTextarea value={value} onChange={onChange} />
);

export const AccountPlanDeleteButton = ({ onDelete }) => (
  <SecondaryButton
    title=""
    onClick={onDelete}
    className="text-gray-300 hover:text-red-500 transition-colors m-w-0 border-none shadow-none p-1 rounded-full"
    icon={<Trash2 size={18} />}
  />
);
